import { LoaderCircle } from "lucide-react";
import { useEffect, useState, useSyncExternalStore } from "react";

type Listener = () => void;

let activeRequests = 0;
let installed = false;
const listeners = new Set<Listener>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function isTrackedRequest(input: RequestInfo | URL) {
  const rawUrl =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input instanceof Request
          ? input.url
          : String(input);

  if (rawUrl.startsWith("/api/")) return true;

  try {
    const url = new URL(rawUrl, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

function installFetchActivityMonitor() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const shouldTrack = isTrackedRequest(input);

    if (!shouldTrack) {
      return nativeFetch(input, init);
    }

    activeRequests += 1;
    emitChange();

    try {
      return await nativeFetch(input, init);
    } finally {
      activeRequests = Math.max(0, activeRequests - 1);
      emitChange();
    }
  };
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return activeRequests;
}

export default function NetworkActivity() {
  const requestCount = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    installFetchActivityMonitor();
  }, []);

  useEffect(() => {
    let showTimer: number | null = null;
    let hideTimer: number | null = null;

    if (requestCount > 0) {
      showTimer = window.setTimeout(() => {
        setVisible(true);
      }, 180);
    } else if (visible) {
      hideTimer = window.setTimeout(() => {
        setVisible(false);
      }, 120);
    } else {
      setVisible(false);
    }

    return () => {
      if (showTimer !== null) window.clearTimeout(showTimer);
      if (hideTimer !== null) window.clearTimeout(hideTimer);
    };
  }, [requestCount, visible]);

  if (!visible) return null;

  return (
    <div className="xNetworkActivity" aria-live="polite" aria-label="Loading content">
      <span className="xNetworkActivitySpinner" aria-hidden="true">
        <LoaderCircle size={14} strokeWidth={2} />
      </span>
      <span>Loading</span>
    </div>
  );
}
